import { Box, Button, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { fontStretch } from "src/design/typography";
import {
  DataTable,
  EmptyState,
  FilterPills,
  InlineAlert,
  Loader,
  StatusChip,
} from "src/grading/components";
import { PageHeader } from "src/grading/layout/PageHeader";
import type { SessionDetail } from "src/models/sessions";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";
import { useListSessionsQuery } from "src/store/reducers/sessions/sessionsApiSlice";

type Filter = "all" | SessionDetail["status"];

/** Sessions in the teacher's or administrator's scope, newest first. */
const SessionsListPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data, isLoading, isError } = useListSessionsQuery();
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  const sessions = useMemo(() => data?.sessions ?? [], [data]);
  /** "Tue, Oct 6, 2026" from a "YYYY-MM-DD" date, read as a calendar date (not UTC). */
  const formatDate = (value: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return value;
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    ).toLocaleDateString(i18n.language, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  const visible = useMemo(
    () =>
      filter === "all" ? sessions : sessions.filter((s) => s.status === filter),
    [sessions, filter],
  );

  const options = [
    {
      value: "all",
      label: t("sessions.filter.all", { defaultValue: "All" }),
    },
    { value: "open", label: t("sessions.status.open") },
    { value: "planned", label: t("sessions.status.planned") },
    { value: "review", label: t("sessions.status.review") },
    { value: "finalized", label: t("sessions.status.finalized") },
  ];

  const planAction = (
    <Button
      variant="contained"
      onClick={() => history.push("/grading/sessions/plan")}
    >
      {t("sessions.plan")}
    </Button>
  );

  if (isLoading) return <Loader />;

  return (
    <Box>
      <PageHeader
        title={t("tabs.sessions")}
        description={t("sessions.listDescription", {
          defaultValue: "Every test day for your classes, newest first.",
        })}
        actions={planAction}
      />
      {isError && (
        <Box sx={{ mb: 2 }}>
          <InlineAlert severity="error">
            {t("sessions.loadError", {
              defaultValue:
                "Could not load sessions. Check your connection and refresh.",
            })}
          </InlineAlert>
        </Box>
      )}
      {sessions.length === 0 ? (
        <Paper>
          <EmptyState
            title={t("console.emptyTitle")}
            body={t("sessions.emptyBody", {
              defaultValue:
                "Plan a session from a class to get started. Planned and finished sessions for your classes appear here.",
            })}
            action={planAction}
          />
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FilterPills
            aria-label={t("sessions.filter.label", {
              defaultValue: "Filter sessions by status",
            })}
            options={options}
            value={filter}
            onChange={(next) => setFilter(next as Filter)}
          />
          <Paper sx={{ overflow: "hidden" }}>
            <DataTable
              minWidth={760}
              aria-label={t("tabs.sessions")}
              rows={visible}
              getRowKey={(s) => s.id}
              onRowClick={(s) => history.push(`/grading/sessions/${s.id}`)}
              columns={[
                {
                  key: "name",
                  header: t("console.session"),
                  render: (s) => (
                    <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                      {s.name}
                    </Typography>
                  ),
                },
                {
                  key: "date",
                  header: t("console.date"),
                  render: (s) => (
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {formatDate(s.date)}
                    </Box>
                  ),
                },
                {
                  key: "window",
                  header: t("sessions.windowLabel"),
                  render: (s) => t(`sessions.window.${s.window}`),
                },
                {
                  key: "students",
                  header: t("console.students"),
                  render: (s) => (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        fontStretch: fontStretch.condensed,
                      }}
                    >
                      {s.rosterSnapshot.length}
                    </Box>
                  ),
                },
                {
                  key: "benchmarks",
                  header: t("console.benchmarks"),
                  render: (s) => (
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {`v${s.benchmarkVersion ?? 1} · ${s.date.slice(0, 4)}`}
                    </Box>
                  ),
                },
                {
                  key: "status",
                  header: t("review.status"),
                  render: (s) => <StatusChip status={s.status} />,
                },
                {
                  key: "go",
                  header: "",
                  align: "right",
                  width: 48,
                  render: () => (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{ color: "text.disabled", fontWeight: 800 }}
                    >
                      →
                    </Box>
                  ),
                },
              ]}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default SessionsListPage;
